import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Table, Form, Spinner, InputGroup, Row, Col, Badge } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import { debounce } from 'lodash';
import useToken from 'hooks/useToken';

registerLocale('es', es);

// Format date to readable Spanish format
const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const time = date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: true }); // Add time
    return `${date.getDate()} de ${months[date.getMonth()]} ${date.getFullYear()} ${time}`;
};

// Format date without time - only day, month, year
const formatDateOnly = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${date.getDate()} de ${months[date.getMonth()]} ${date.getFullYear()}`;
};

const AkasiaPosLicenses = () => {
    const { token } = useToken();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Filters
    const [licenseType, setLicenseType] = useState('All');
    const [licenseStatus, setLicenseStatus] = useState('All');
    const [dateFilterType, setDateFilterType] = useState('LastConnection');

    // Initialize with Last 30 Days by default
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return d;
    });
    const [endDate, setEndDate] = useState(new Date());
    const [appliedDateRange, setAppliedDateRange] = useState(null);

    const [pageNumber, setPageNumber] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const pageSize = 25;

    const fetchData = useCallback(async (page, search, type, status, dateType, start, end, append = false) => {
        if (!token) return;

        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const response = await fetch('/api/AkasiaPosDashboard/GetLicenses', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    pageNumber: page,
                    pageSize: pageSize,
                    searchTerm: search || null,
                    licenseType: type,
                    licenseStatus: status,
                    dateFilterType: dateType,
                    startDate: start ? start.toISOString() : null,
                    endDate: end ? end.toISOString() : null
                })
            });

            const result = await response.json();
            if (result.iResponseType === 1 && result.detalles) {
                const parsed = JSON.parse(result.detalles);

                // table1 = response status, table2 = actual data
                const table = parsed.table2 || [];

                if (table.length > 0) {
                    if (append) {
                        setData(prev => [...prev, ...table]);
                    } else {
                        setData(table);
                    }

                    setHasMore(page * pageSize < (table[0]?.iTotalCount || 0));
                } else {
                    if (!append) {
                        setData([]);
                    }
                    setHasMore(false);
                }

                // Parse Applied Dates (Table 3)
                if (parsed.table3 && parsed.table3.length > 0) {
                    const dates = parsed.table3[0];
                    if (dates.dAppliedStartDate && dates.dAppliedEndDate) {
                        setAppliedDateRange({
                            start: dates.dAppliedStartDate,
                            end: dates.dAppliedEndDate
                        });
                    }
                }
            } else {
                setError('Error loading licenses');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [token, pageSize]);

    // Debounced search
    const debouncedFetch = useCallback(
        debounce((search, type, status, dateType, start, end) => {
            setPageNumber(1);
            setData([]);
            fetchData(1, search, type, status, dateType, start, end, false);
        }, 500),
        [fetchData]
    );

    // Initial load and filter changes
    useEffect(() => {
        if (token) {
            debouncedFetch(searchTerm, licenseType, licenseStatus, dateFilterType, startDate, endDate);
        }
    }, [token, searchTerm, licenseType, licenseStatus, dateFilterType, startDate, endDate, debouncedFetch]);

    // Infinite scroll handler
    const handleScroll = useCallback(() => {
        const container = tableContainerRef.current;
        if (!container || loadingMore || !hasMore) return;

        const scrollPercentage = (container.scrollTop + container.clientHeight) / container.scrollHeight;
        if (scrollPercentage > 0.8) {
            const nextPage = pageNumber + 1;
            setPageNumber(nextPage);
            fetchData(nextPage, searchTerm, licenseType, licenseStatus, dateFilterType, startDate, endDate, true);
        }
    }, [loadingMore, hasMore, pageNumber, searchTerm, licenseType, licenseStatus, dateFilterType, startDate, endDate, fetchData]);

    useEffect(() => {
        const container = tableContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            return () => container.removeEventListener('scroll', handleScroll);
        }
    }, [handleScroll]);

    return (
        <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom">
                <div className="mb-3">
                    <div className="d-flex align-items-center">
                        <h5 className="mb-0 fw-bold">
                            <i className="bi bi-card-checklist text-success me-2"></i>
                            Licencias
                        </h5>
                        {appliedDateRange && (
                            <span className="ms-auto text-dark fw-medium" style={{ fontSize: '0.9rem' }}>
                                <i className="bi bi-calendar3 me-1"></i>
                                {formatDate(appliedDateRange.start)} - {formatDate(appliedDateRange.end)}
                            </span>
                        )}
                    </div>
                    <p className="text-muted small mb-0 mt-1">Gestiona y filtra licencias de POS independientemente de los usuarios</p>
                </div>

                {/* Filters Row */}
                <Row className="g-2 mb-3">
                    <Col md={2}>
                        <Form.Select
                            size="sm"
                            value={licenseType}
                            onChange={(e) => setLicenseType(e.target.value)}
                            className="border-primary"
                        >
                            <option value="All">Tipos: Todos</option>
                            <option value="Trial">Prueba</option>
                            <option value="Paid">Pagada</option>
                            <option value="Linked">Vinculadas</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <Form.Select
                            size="sm"
                            value={licenseStatus}
                            onChange={(e) => setLicenseStatus(e.target.value)}
                            className="border-success"
                        >
                            <option value="All">Estado: Todos</option>
                            <option value="Active">Activa</option>
                            <option value="Inactive">Inactiva</option>
                        </Form.Select>
                    </Col>
                    <Col md={2}>
                        <InputGroup size="sm">
                            <Form.Select
                                value={dateFilterType}
                                onChange={(e) => setDateFilterType(e.target.value)}
                                className="border-warning"
                            >
                                <option value="LastConnection">Última Conexión</option>
                                <option value="CreationDate">Fecha Creación</option>
                            </Form.Select>
                        </InputGroup>
                    </Col>
                    <Col md={2} style={{ position: 'relative', zIndex: 1050 }}>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => setStartDate(date)}
                            className="form-control form-control-sm"
                            dateFormat="dd 'de' MMMM yyyy"
                            locale="es"
                            placeholderText="Desde"
                            todayButton="Hoy"
                            isClearable
                            popperPlacement="bottom-start"
                        />
                    </Col>
                    <Col md={2} style={{ position: 'relative', zIndex: 1050 }}>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => setEndDate(date)}
                            className="form-control form-control-sm"
                            dateFormat="dd 'de' MMMM yyyy"
                            locale="es"
                            placeholderText="Hasta"
                            todayButton="Hoy"
                            isClearable
                            popperPlacement="bottom-start"
                        />
                    </Col>
                    <Col md={2}>
                        <InputGroup size="sm">
                            <InputGroup.Text className="bg-white border-end-0">
                                <i className="bi bi-search text-muted"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder="Buscar..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border-start-0 ps-0"
                            />
                        </InputGroup>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body className="p-0">
                <div
                    ref={tableContainerRef}
                    className="table-responsive"
                    style={{ maxHeight: '600px', overflowY: 'auto' }}
                >
                    <Table hover className="mb-0">
                        <thead className="bg-light sticky-top" style={{ top: 0, zIndex: 1 }}>
                            <tr>
                                <th className="ps-4 py-3 border-bottom">ID</th>
                                <th className="py-3 border-bottom">Empresa</th>
                                <th className="py-3 border-bottom">Vigencia</th>
                                <th className="py-3 border-bottom">Tipo</th>
                                <th className="py-3 border-bottom">Estado</th>
                                <th className="py-3 border-bottom">Fecha ({dateFilterType === 'LastConnection' ? 'Conexión' : 'Creación'})</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && data.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <Spinner animation="border" variant="success" />
                                        <p className="text-muted mt-2 mb-0">Cargando licencias...</p>
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <i className="bi bi-exclamation-circle text-danger fs-1"></i>
                                        <p className="text-danger mt-2 mb-0">{error}</p>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-5">
                                        <i className="bi bi-inbox text-muted fs-1"></i>
                                        <p className="text-muted mt-2 mb-0">No se encontraron licencias</p>
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {data.map((item, index) => (
                                        <tr key={`${item.vUUIDEmpresa}-${index}`}>
                                            <td className="ps-4 py-3">
                                                <div className="d-flex flex-column gap-1">
                                                    <div>
                                                        <small className="text-dark d-block fw-semibold" style={{ fontSize: '0.85rem' }}>Cliente:</small>
                                                        {item.idClienteAkasia ? (
                                                            <a href={`/clientes/detalle/${item.idClienteAkasia}`} className="text-decoration-none">
                                                                <Badge bg="primary" className="cursor-pointer" style={{ fontSize: '0.9rem' }}>
                                                                    {item.idClienteAkasia}
                                                                </Badge>
                                                            </a>
                                                        ) : (
                                                            <Badge bg="secondary" style={{ fontSize: '0.9rem' }}>N/A</Badge>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <small className="text-dark d-block fw-semibold" style={{ fontSize: '0.85rem' }}>Licencia:</small>
                                                        <a href={`/licencias/detalle/${item.idLicencia}`} className="text-decoration-none">
                                                            <Badge bg="light" text="dark" className="border cursor-pointer" style={{ fontSize: '0.9rem' }}>
                                                                {item.idLicencia}
                                                            </Badge>
                                                        </a>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                <div className="fw-bold text-dark">
                                                    {item.vNombreEmpresa || 'Sin nombre'}
                                                </div>
                                                <small className="text-muted" style={{ fontSize: '0.75rem' }}>{item.vUUIDEmpresa}</small>
                                            </td>
                                            <td className="py-3">
                                                <div className="d-flex flex-column gap-1">
                                                    <div>
                                                        <small className="text-dark d-block fw-semibold" style={{ fontSize: '0.85rem' }}>Inicio:</small>
                                                        <span className="text-muted" style={{ fontSize: '0.9rem' }}>{formatDateOnly(item.dInicioLicenciaUso)}</span>
                                                    </div>
                                                    <div>
                                                        <small className="text-dark d-block fw-semibold" style={{ fontSize: '0.85rem' }}>Fin:</small>
                                                        {item.dFinLicenciaUso ? (
                                                            <span className={`${new Date(item.dFinLicenciaUso) < new Date() ? 'text-danger fw-bold' : 'text-muted'}`} style={{ fontSize: '0.9rem' }}>
                                                                {formatDateOnly(item.dFinLicenciaUso)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted" style={{ fontSize: '0.9rem' }}>-</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3">
                                                {item.bIsPeriodoPrueba ? (
                                                    <Badge bg="warning" text="dark">
                                                        <i className="bi bi-clock-history me-1"></i>
                                                        Prueba
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="success">
                                                        <i className="bi bi-check-circle me-1"></i>
                                                        Pagada
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="py-3">
                                                {item.bIsActive ? (
                                                    <Badge bg="success">
                                                        <i className="bi bi-check-circle me-1"></i>
                                                        Activa
                                                    </Badge>
                                                ) : (
                                                    <Badge bg="secondary">
                                                        <i className="bi bi-x-circle me-1"></i>
                                                        Inactiva
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="py-3">
                                                <span className="text-muted small">
                                                    <i className={`bi bi-${dateFilterType === 'LastConnection' ? 'activity' : 'calendar-plus'} me-1`}></i>
                                                    {dateFilterType === 'LastConnection'
                                                        ? (item.dUltimaActividad ? formatDate(item.dUltimaActividad) : 'Sin conexión')
                                                        : formatDate(item.dCreatedDate)
                                                    }
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </Table>
                    {loadingMore && (
                        <div className="text-center py-3 border-top">
                            <Spinner animation="border" size="sm" variant="success" className="me-2" />
                            <span className="text-muted small">Cargando más...</span>
                        </div>
                    )}
                </div>
            </Card.Body>
            {!loading && data.length > 0 && (
                <Card.Footer className="bg-light text-center text-muted small">
                    {hasMore ? (
                        <>
                            <i className="bi bi-arrow-down me-1"></i>
                            Desplázate para ver más
                        </>
                    ) : (
                        <>
                            <i className="bi bi-check-circle me-1"></i>
                            Todas las licencias cargadas
                        </>
                    )}
                </Card.Footer>
            )}
        </Card>
    );
};

export default AkasiaPosLicenses;
